import type { AxiosInstance } from 'axios';
import type { NewsPost, NewsPostSummary, CreateNewsPostRequest, UpdateNewsPostRequest, PageResponse } from './types';
import { NewsCategory, NewsStatus } from './types';

export function createNewsService(api: AxiosInstance) {
  return {
    /** Öffentliche Liste veröffentlichter Artikel (ohne content), neueste zuerst. */
    getAll: async (page: number = 0, size: number = 20, category?: NewsCategory): Promise<PageResponse<NewsPostSummary>> => {
      const params: Record<string, unknown> = { page, size };
      if (category) params.category = category;
      const response = await api.get('/news', { params });
      return response.data;
    },

    getBySlug: async (slug: string): Promise<NewsPost> => {
      const response = await api.get(`/news/${slug}`);
      return response.data;
    },

    /**
     * Veröffentlichte Artikel, die an diesem Event hängen — die Gegenrichtung der Verknüpfung,
     * die die Event-Detailseite anzeigt. Neueste zuerst.
     */
    getForEvent: async (eventId: number, size: number = 3): Promise<PageResponse<NewsPostSummary>> => {
      const response = await api.get('/news', { params: { page: 0, size, eventId } });
      return response.data;
    },

    // ---- Admin ----

    getAllAdmin: async (page: number = 0, size: number = 50, status?: NewsStatus): Promise<PageResponse<NewsPost>> => {
      const params: Record<string, unknown> = { page, size };
      if (status) params.status = status;
      const response = await api.get('/news/admin', { params });
      return response.data;
    },

    create: async (data: CreateNewsPostRequest): Promise<NewsPost> => {
      const response = await api.post('/news', data);
      return response.data;
    },

    update: async (id: number, data: UpdateNewsPostRequest): Promise<NewsPost> => {
      const response = await api.put(`/news/${id}`, data);
      return response.data;
    },

    updateStatus: async (id: number, status: NewsStatus): Promise<NewsPost> => {
      const response = await api.patch(`/news/${id}/status`, { status });
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await api.delete(`/news/${id}`);
    },

    /** Cover-Bild hochladen (JPEG/PNG/WebP, max. 2 MB); Response enthält die neue coverImageUrl. */
    uploadCover: async (id: number, file: Blob, filename?: string): Promise<NewsPost> => {
      const form = new FormData();
      form.append('file', file, filename ?? 'cover');
      const response = await api.post(`/news/${id}/cover`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    deleteCover: async (id: number): Promise<NewsPost> => {
      const response = await api.delete(`/news/${id}/cover`);
      return response.data;
    },
  };
}

export type NewsService = ReturnType<typeof createNewsService>;
